/**
 * @fileoverview Module for `src/page/Header.js`.
 */

import axios from 'axios';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import PropTypes from 'prop-types';
import { Component, Fragment } from 'react';
import { Link, NavLink } from 'react-router-dom';

import Lightbox from './Lightbox';
import Banner from './Banner';
import logger from '../lib/logger';
import DropdownNavItem from './DropdownNavItem';
import { AuthContext } from '../contexts/AuthContext';
import ClickToAgree from '../components/ClickToAgree';

import '../scss/page/_header.scss';
import '../scss/page/_dropdown-nav-item.scss';

/**
 * Global site header with authenticated navigation and announcements.
 */
class Header extends Component {
  static contextType = AuthContext;

  /**
   * Initializes navbar collapse, announcement, and lightbox state.
   *
   * @param {Object} props - Component props.
   * @param {string} [props.path] - Current route path used to hide
   *   authenticated nav on the verify page.
   * @param {boolean} [props.showBanner=true] - Whether to render
   *   the announcement banner.
   */
  constructor(props) {
    super(props);
    this.state = {
      navBarCollapsed: true,
      announcementLightbox: null,
      announcementBanner: null,
      resetLightBox: false,
    };
  }

  /**
   * Loads banner and lightbox announcements on mount.
   */
  componentDidMount() {
    this.getCurrentAnnouncement().catch(logger.error);
    this.getCurrentBannerAnnouncement().catch(logger.error);
  }

  /**
   * Loads the active banner announcement payload.
   *
   * @returns {Promise<void>} Resolves after banner state update.
   */
  async getCurrentBannerAnnouncement() {
    const result = await axios.get(`${ process.env.REACT_APP_API_URL }/v1/announcements/banner/current`);
    if (result && result.data) {
      const announcementBanner = { ...this.state.announcementBanner, ...result.data };
      this.setState({ announcementBanner });
    } else {
      this.setState({ announcementBanner: null });
    }
  }

  /**
   * Loads the active lightbox announcement payload.
   *
   * @returns {Promise<void>} Resolves after lightbox state update.
   */
  async getCurrentAnnouncement() {
    const result = await axios.get(`${ process.env.REACT_APP_API_URL }/v1/announcements/lightbox/current`);
    const announcementLightbox = { ...this.state.announcementLightbox };

    announcementLightbox.id = result.data.id;
    announcementLightbox.header = result.data.header;
    announcementLightbox.content = result.data.content;
    announcementLightbox.ctaText = result.data.ctaText;
    announcementLightbox.ctaUrl = result.data.ctaUrl;
    announcementLightbox.secondaryHeader = result.data.secondaryHeader;
    announcementLightbox.secondaryContent = result.data.secondaryContent;
    announcementLightbox.navBarText = result.data.navBarText;

    this.setState({ announcementLightbox });
  }

  /**
   * Redirects to the Auth0 login page.
   */
  login = () => this.context.login(true);

  /**
   * Toggles the mobile navbar between collapsed and expanded states.
   */
  toggleNavbarCollapse = () => {
    this.setState({ navBarCollapsed: !this.state.navBarCollapsed });
  };

  /**
   * Re-opens the announcement lightbox and forces a re-render.
   */
  resetAnnouncement = () => {
    this.setState({ resetLightBox: true });
    this.forceUpdate();
  };

  /**
   * Clears the lightbox reset flag after the lightbox closes.
   */
  onLightboxClose = () => {
    this.setState({ resetLightBox: false });
  };

  /**
   * Renders the site header with navigation links, profile controls,
   * announcement banner, and lightbox overlay.
   *
   * @returns {JSX.Element} The site header.
   */
  render() {
    const showAdmin = this.context.isAuthenticated() && this.context.isAdmin() && this.props.path !== '/verify';

    // zIndex is specified here so that in the admin console, the Header does not cover a full screen modal
    return (
      <header>
        <ClickToAgree auth={ this.context } />
        <nav className='navbar navbar-expand-lg' style={{ zIndex: 1 }}>
          <Link className='logo navbar-brand' id='votefwdLogo' to='/'>
            <img alt='Vote Forward' src='/images/vf-logo-2026.svg' />
          </Link>
          <button
            aria-label='Toggle navigation'
            className='navbar-toggler'
            onClick={ this.toggleNavbarCollapse }
            type='button'
          >
            <span className='navbar-toggler-icon' />
          </button>
          <div className={ `${ this.state.navBarCollapsed ? 'collapse' : '' } navbar-collapse flex-row-reverse` } id='navbarNavAltMarkup'>
            <div className='navbar-nav'>
              <DropdownNavItem
                items={ [
                  { label: 'Mission', href: 'https://votefwd.org/about' },
                  { label: 'Impact', href: 'https://votefwd.org/impact' },
                  { label: 'Research', href: 'https://votefwd.org/research' },
                ] }
                label='About'
              />
              <a className='nav-item nav-link text-white' href='https://votefwd.org/get-involved'>Get Involved</a>
              { (this.context.isAuthenticated() && this.props.path !== '/verify')
                ? (
                  <Fragment>
                    <NavLink activeClassName='active' className='nav-item nav-link text-white' to='/dashboard'>Dashboard</NavLink>
                    <button className='nav-item nav-link text-white btn-link' id='logoutButton' onClick={ this.context.logout }>Log Out</button>
                  </Fragment>
                  )
                : (
                  <button className='nav-item nav-link text-white btn-link' id='loginButton' onClick={ this.login }>Dashboard</button>
                  ) }
              { showAdmin && (
                <Link className='nav-item nav-link text-white' to='/admin'>
                  Admin
                </Link>
              ) }
              { this.state.announcementLightbox && this.state.announcementLightbox.navBarText && (
                <button
                  className='nav-item nav-link text-white btn-link'
                  onClick={ this.resetAnnouncement }
                  type='button'
                >
                  <NotificationsActiveIcon />
                  <span className='d-none d-xl-inline-block'>
                    &nbsp;{ this.state.announcementLightbox.navBarText }
                  </span>
                </button>
              ) }
              <div className='nav-item nav-button nav-link me-0'>
                <a className='btn btn-primary-blue' href='https://secure.actblue.com/donate/vote-forward-c4?refcode=webheader' rel='noreferrer noopener' target='_blank'>
                  Donate
                </a>
              </div>
            </div>
          </div>
        </nav>
        { this.props.showBanner && this.state.announcementBanner ? <Banner data={ this.state.announcementBanner } /> : null }
        { this.state.announcementLightbox ? <Lightbox announcement={ this.state.announcementLightbox } onLightboxClose={ this.onLightboxClose } resetLightBox={ this.state.resetLightBox } /> : null }
      </header>
    );
  }
}

Header.defaultProps = {
  showBanner: true,
};

Header.propTypes = {
  path: PropTypes.string,
  showBanner: PropTypes.bool,
};

export default Header;
